import { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { faBuilding } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";

import { useAppDispatch, useAppSelector } from "@/redux/hooks";

import { listSelector, setCurrentLeadIndex } from "@/pages/Private/redux/list/list.slice";

import { SignalFilterStatus } from "@/enum/list.enum";

import { ProspectSignalGroup } from "./ProspectSignalGroup";
import {
	getCombinedTrackerSignals,
	resolveSignalFilterMode,
	signalMatchesFilter,
	type ProspectSignalGroup as ProspectSignalGroupType,
	type TrackerSignalResultAdditional,
} from "./combinedSignalFeedUtils";

interface EnrichedSignal {
	signal: TrackerSignalResultAdditional;
	inboxIndex: number;
}

interface CombinedSignalFeedProps {
	onReachOutToProspect?: (inboxIndex: number, signal: TrackerSignalResultAdditional) => void;
	onGenerateDraftMessages?: (inboxIndex: number, signal: TrackerSignalResultAdditional) => void;
	signalFilterStatus?: SignalFilterStatus[];
}
export const CombinedSignalFeed: FunctionComponent<CombinedSignalFeedProps> = ({
	onReachOutToProspect,
	onGenerateDraftMessages,
	signalFilterStatus,
}) => {
	const dispatch = useAppDispatch();
	const { inbox, currentLeadIndex } = useAppSelector(listSelector);
	const [expandedSummaryIds, setExpandedSummaryIds] = useState<Set<number>>(new Set());
	const [expandedProspectIndexes, setExpandedProspectIndexes] = useState<Set<number>>(new Set());

	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const cardRefs = useRef<Map<string, HTMLDivElement>>(new Map());
	const isScrollSyncingRef = useRef(false);
	/** Tracks whether the last currentLeadIndex change came from the observer (true)
	 *  or from a user click in the lead list (false). */
	const changeFromObserverRef = useRef(false);

	const prospectSignalGroups = useMemo<ProspectSignalGroupType[]>(() => {
		const filterMode = resolveSignalFilterMode(signalFilterStatus);

		return inbox.reduce<ProspectSignalGroupType[]>((result, listPerson, idx) => {
			const signals = getCombinedTrackerSignals(
				listPerson.trackerSignalResult ? [listPerson.trackerSignalResult] : [],
				listPerson.prospect?.organization?.trackerSignalResults,
				listPerson.prospect?.trackerSignalResults
			).filter((signal) => signalMatchesFilter(signal, filterMode));

			if (signals.length === 0) {
				return result;
			}

			result.push({
				inboxIndex: idx,
				listPersonId: listPerson.id,
				prospectId: listPerson.prospect?.id,
				prospectName: listPerson.prospect?.name ?? "Unknown",
				organizationName: listPerson.prospect?.organization?.name ?? "",
				signals,
			});

			return result;
		}, []);
	}, [inbox, signalFilterStatus]);

	const enrichedSignals = useMemo<EnrichedSignal[]>(
		() =>
			prospectSignalGroups.flatMap(({ inboxIndex, signals }) =>
				signals.map((signal) => ({ signal, inboxIndex }))
			),
		[prospectSignalGroups]
	);

	const toggleSummaryExpanded = (signalId: number) => {
		setExpandedSummaryIds((prev) => {
			const newSet = new Set(prev);

			if (newSet.has(signalId)) {
				newSet.delete(signalId);
			} else {
				newSet.add(signalId);
			}

			return newSet;
		});
	};

	const scrollLeadListTo = useCallback((idx: number, triggerLazyLoad = false) => {
		const leadsContainer = document.querySelector("#leads-container");

		if (!leadsContainer) {
			return;
		}

		const leadItem = leadsContainer.querySelectorAll(".lead-item")[idx];

		if (leadItem) {
			const containerRect = leadsContainer.getBoundingClientRect();
			const itemRect = leadItem.getBoundingClientRect();

			// Item is above the visible area
			if (itemRect.top < containerRect.top) {
				leadsContainer.scrollTo({
					top: leadsContainer.scrollTop + (itemRect.top - containerRect.top),
					behavior: "smooth",
				});
			}
			// Item is below the visible area
			else if (itemRect.bottom > containerRect.bottom) {
				leadsContainer.scrollTo({
					top: leadsContainer.scrollTop + (itemRect.bottom - containerRect.bottom),
					behavior: "smooth",
				});
			}
		} else if (triggerLazyLoad) {
			// Item not rendered yet → scroll the leads container to the very bottom
			// so the lazy-load sentinel becomes visible and triggers a fetch
			leadsContainer.scrollTo({
				top: leadsContainer.scrollHeight,
				behavior: "smooth",
			});
		}
	}, []);

	const selectLeadFromSignal = useCallback(
		(inboxIndex: number) => {
			changeFromObserverRef.current = true;
			dispatch(setCurrentLeadIndex(inboxIndex));
			scrollLeadListTo(inboxIndex);
		},
		[dispatch, scrollLeadListTo]
	);

	const currentLeadIndexRef = useRef(currentLeadIndex);

	useEffect(() => {
		currentLeadIndexRef.current = currentLeadIndex;
	}, [currentLeadIndex]);

	useEffect(() => {
		const container = scrollContainerRef.current;

		if (!container || enrichedSignals.length === 0) {
			return;
		}

		let debounceTimer: ReturnType<typeof setTimeout> | null = null;

		const handleScroll = () => {
			if (debounceTimer) {
				clearTimeout(debounceTimer);
			}

			debounceTimer = setTimeout(() => {
				if (isScrollSyncingRef.current) {
					return;
				}

				const containerRect = container.getBoundingClientRect();

				const isAtBottom =
					container.scrollHeight - container.scrollTop - container.clientHeight < 20;

				if (isAtBottom) {
					const leadsContainer = document.querySelector("#leads-container");

					if (leadsContainer) {
						leadsContainer.scrollTo({
							top: leadsContainer.scrollHeight,
							behavior: "smooth",
						});
					}

					return;
				}

				const bestCard: { idx: number; distance: number } = { idx: -1, distance: Infinity };
				let found = false;

				cardRefs.current.forEach((el) => {
					const cardRect = el.getBoundingClientRect();
					const distance = cardRect.top - containerRect.top;

					if (cardRect.bottom <= containerRect.top) {
						return;
					}

					const absDist = Math.abs(distance);

					if (!found || absDist < bestCard.distance) {
						const idx = Number(el.getAttribute("data-inbox-index"));

						if (!isNaN(idx)) {
							bestCard.idx = idx;
							bestCard.distance = absDist;
							found = true;
						}
					}
				});

				if (found && bestCard.idx !== currentLeadIndexRef.current) {
					changeFromObserverRef.current = true;
					dispatch(setCurrentLeadIndex(bestCard.idx));
					scrollLeadListTo(bestCard.idx);
				}
			}, 100);
		};

		container.addEventListener("scroll", handleScroll, { passive: true });

		return () => {
			container.removeEventListener("scroll", handleScroll);

			if (debounceTimer) {
				clearTimeout(debounceTimer);
			}
		};
	}, [enrichedSignals, inbox.length, dispatch, scrollLeadListTo]);

	const scrollToProspect = useCallback(
		(inboxIndex: number) => {
			const container = scrollContainerRef.current;
			const firstCard = prospectSignalGroups.find((group) => group.inboxIndex === inboxIndex)
				?.signals[0];

			if (!firstCard || !container) {
				return;
			}

			const key = `${firstCard.id}-${inboxIndex}`;
			const el = cardRefs.current.get(key);

			if (el) {
				isScrollSyncingRef.current = true;

				const containerRect = container.getBoundingClientRect();
				const elRect = el.getBoundingClientRect();

				container.scrollTo({
					top: container.scrollTop + (elRect.top - containerRect.top),
					behavior: "smooth",
				});

				setTimeout(() => {
					isScrollSyncingRef.current = false;
				}, 600);
			}
		},
		[prospectSignalGroups]
	);

	const scrollToProspectRef = useRef(scrollToProspect);

	useEffect(() => {
		scrollToProspectRef.current = scrollToProspect;
	}, [scrollToProspect]);

	useEffect(() => {
		if (changeFromObserverRef.current) {
			changeFromObserverRef.current = false;

			return;
		}

		scrollToProspectRef.current(currentLeadIndex);
	}, [currentLeadIndex]);

	const setCardRef = useCallback(
		(key: string) => (el: HTMLDivElement | null) => {
			if (el) {
				cardRefs.current.set(key, el);
			} else {
				cardRefs.current.delete(key);
			}
		},
		[]
	);

	const handleReachOutToProspect = useCallback(
		(inboxIndex: number, signal: TrackerSignalResultAdditional) => {
			selectLeadFromSignal(inboxIndex);
			onReachOutToProspect?.(inboxIndex, signal);
		},
		[onReachOutToProspect, selectLeadFromSignal]
	);

	const handleGenerateDraftMessages = useCallback(
		(inboxIndex: number, signal: TrackerSignalResultAdditional) => {
			selectLeadFromSignal(inboxIndex);
			onGenerateDraftMessages?.(inboxIndex, signal);
		},
		[onGenerateDraftMessages, selectLeadFromSignal]
	);

	const toggleProspectExpanded = useCallback(
		(inboxIndex: number) => {
			selectLeadFromSignal(inboxIndex);
			setExpandedProspectIndexes((prev) => {
				const newSet = new Set(prev);

				if (newSet.has(inboxIndex)) {
					newSet.delete(inboxIndex);
				} else {
					newSet.add(inboxIndex);
				}

				return newSet;
			});
		},
		[selectLeadFromSignal]
	);

	if (enrichedSignals.length === 0) {
		return (
			<div className="mb-8 flex flex-col">
				<div className="flex flex-col items-center justify-center py-12 text-gray-500">
					<Icon className="w-12 h-12 mb-4 text-gray-300" icon={faBuilding} />
					<p className="text-sm">No signals found</p>
				</div>
			</div>
		);
	}

	return (
		<div className="flex flex-col p-4 h-full">
			<div
				ref={scrollContainerRef}
				className="flex flex-col gap-4 overflow-y-auto max-h-full scrollbar-hide"
			>
				{prospectSignalGroups.map((group) => (
					<ProspectSignalGroup
						key={`${group.inboxIndex}-${group.signals[0].id}`}
						currentLeadIndex={currentLeadIndex}
						expandedSummaryIds={expandedSummaryIds}
						group={group}
						isExpanded={expandedProspectIndexes.has(group.inboxIndex)}
						setCardRef={setCardRef}
						onGenerateDrafts={onGenerateDraftMessages ? handleGenerateDraftMessages : undefined}
						onReachOut={onReachOutToProspect ? handleReachOutToProspect : undefined}
						onSelectLead={selectLeadFromSignal}
						onToggleSignals={toggleProspectExpanded}
						onToggleSummary={toggleSummaryExpanded}
					/>
				))}
			</div>
		</div>
	);
};
